import { useSelector } from 'react-redux';
import {
  useMutation, useQueries, useQuery, useQueryClient,
} from '@tanstack/react-query';
import { selectUser } from 'redux/selectors/auth';
import { ManagedStartupType } from '../types';
import {
  decidePortalQuote, getPortalRequest, addPortalOrder, getPortalPortfolio, getPortalRequestItems, getPortalRequests,
  getPortalService, getPortalServices, submitPortalRequest, changePortalDraft,
} from './api';
import {
  PortalQuoteDecision, PortalDraftChange, PortalOrder, PortalRequestItem, PortalScope,
} from './types';
import { portalKeys } from './utils';

export const usePortalScope = (): PortalScope => {
  const user = useSelector(selectUser);
  return { userId: String(user.userId || ''), ventureBuilderId: user.ventureBuilderId || 0 };
};

export const usePortalPortfolio = (scope: PortalScope) => useQuery({
  queryKey: portalKeys.portfolio(scope), queryFn: () => getPortalPortfolio(scope), retry: false,
});

export const usePortalServices = (scope: PortalScope) => useQuery({
  queryKey: portalKeys.services(scope), queryFn: getPortalServices, retry: false,
});

export const usePortalService = (scope: PortalScope, id: number) => useQuery({
  queryKey: portalKeys.service(scope, id), queryFn: () => getPortalService(id), retry: false,
});

export const usePortalRequests = (scope: PortalScope, startups: ManagedStartupType[]) => useQueries({
  queries: startups.map((startup) => ({
    queryKey: portalKeys.requests(scope, startup.id),
    queryFn: () => getPortalRequests(startup),
    retry: false,
  })),
});

export const usePortalRequestItems = (scope: PortalScope, startupId: number, requestId: number) => useQuery({
  queryKey: portalKeys.items(scope, startupId, requestId),
  queryFn: () => getPortalRequestItems(startupId, requestId),
  retry: false,
});

const useInvalidatePortal = (scope: PortalScope) => {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: portalKeys.scope(scope) });
};

export const useAddPortalOrder = (scope: PortalScope) => {
  const invalidate = useInvalidatePortal(scope);
  return useMutation({
    mutationFn: (order: PortalOrder) => addPortalOrder(scope, order),
    // Even a failed item save can leave a draft on the server; always refresh Requests.
    onSettled: invalidate,
  });
};

export const useSubmitPortalRequest = (scope: PortalScope) => {
  const invalidate = useInvalidatePortal(scope);
  return useMutation({
    mutationFn: ({ startupId, requestId, reviewedItems }: {
      startupId: number, requestId: number, reviewedItems: PortalRequestItem[],
    }) => (
      submitPortalRequest(scope, startupId, requestId, reviewedItems)
    ),
    onSettled: invalidate,
  });
};

export const useChangePortalDraft = (scope: PortalScope) => {
  const invalidate = useInvalidatePortal(scope);
  const client = useQueryClient();
  return useMutation({
    mutationFn: (action: PortalDraftChange) => changePortalDraft(scope, action),
    onSettled: (_data, error, action) => {
      if (error || action.change.kind !== 'discard') return invalidate();
      // The drawer stays mounted until the mutation callback closes it. Do not
      // refetch its deleted request while refreshing the remaining portfolio.
      const deletedItems = JSON.stringify(portalKeys.items(scope, action.startupId, action.requestId));
      return client.invalidateQueries({
        queryKey: portalKeys.scope(scope),
        predicate: (query) => JSON.stringify(query.queryKey) !== deletedItems,
      });
    },
  });
};

export const usePortalRequest = (scope: PortalScope, startupId: number, requestId: number) => useQuery({
  queryKey: [...portalKeys.requests(scope, startupId), 'details', requestId],
  queryFn: () => getPortalRequest(startupId, requestId),
  retry: false,
});

export const useDecidePortalQuote = (scope: PortalScope) => {
  const invalidate = useInvalidatePortal(scope);
  return useMutation({
    mutationFn: (decision: PortalQuoteDecision) => decidePortalQuote(scope, decision),
    onSettled: invalidate,
  });
};
